import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Trophy, Medal, Package, Star, CheckCircle2, Sparkles, Flame,
  MessageCircle, Layers, Lock,
} from "lucide-react";

const ICON_MAP = {
  Trophy, Medal, Package, Star, CheckCircle2, Sparkles, Flame,
  MessageCircle, Layers,
};

const TINT_CLASS = {
  amber: { ring: "from-amber-500 to-rose-500", bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30" },
  rose: { ring: "from-rose-500 to-amber-500", bg: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/30" },
  emerald: { ring: "from-emerald-500 to-sky-500", bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30" },
  purple: { ring: "from-purple-500 to-rose-500", bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30" },
  sky: { ring: "from-sky-500 to-emerald-500", bg: "bg-sky-500/10", text: "text-sky-400", border: "border-sky-500/30" },
  orange: { ring: "from-orange-500 to-amber-500", bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30" },
  violet: { ring: "from-violet-500 to-purple-500", bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/30" },
};

function BadgeIcon({ badge, locked = false, size = 44 }) {
  const Icon = ICON_MAP[badge.icon] || Trophy;
  const tint = TINT_CLASS[badge.tint] || TINT_CLASS.amber;
  const s = size;
  return (
    <div
      className={`relative rounded-xl flex items-center justify-center flex-shrink-0 border ${
        locked ? "bg-zinc-900 border-zinc-800 grayscale opacity-60" : `${tint.bg} ${tint.border}`
      }`}
      style={{ width: s, height: s }}
      title={badge.label}
    >
      <Icon className={locked ? "text-zinc-600" : tint.text} style={{ width: s * 0.5, height: s * 0.5 }} />
      {locked && <Lock className="absolute -bottom-1 -right-1 w-3 h-3 text-zinc-500 bg-zinc-950 rounded-full p-0.5" />}
    </div>
  );
}

export default function StatsPanel({ showBadgeGallery = false }) {
  const [stats, setStats] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevBadges = useRef([]);

  const fetchStats = useCallback(async () => {
    try {
      const [{ data: s }, { data: b }] = await Promise.all([
        api.get("/stats/me"),
        api.get("/stats/badges"),
      ]);
      setStats(s);
      setAllBadges(b.badges || []);
      // Fire toast for freshly unlocked (compared to previous state)
      const prevSet = new Set(prevBadges.current);
      const newly = (s.badges || []).filter((id) => !prevSet.has(id));
      if (prevBadges.current.length > 0 && newly.length > 0) {
        newly.forEach((id) => {
          const b0 = (b.badges || []).find((x) => x.id === id);
          if (b0) toast.success(`Badge unlocked: ${b0.label}`, { description: b0.description });
        });
      }
      prevBadges.current = s.badges || [];
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Light polling so scan/haul actions update the panel without page reload
  useEffect(() => {
    const id = setInterval(fetchStats, 30000);
    return () => clearInterval(id);
  }, [fetchStats]);

  const unlockedSet = useMemo(() => new Set(stats?.badges || []), [stats]);
  const unlocked = useMemo(() => allBadges.filter((b) => unlockedSet.has(b.id)), [allBadges, unlockedSet]);
  const locked = useMemo(() => allBadges.filter((b) => !unlockedSet.has(b.id)), [allBadges, unlockedSet]);

  if (loading) {
    return <div data-testid="stats-panel-loading" className="h-24 rounded-xl bg-zinc-900/60 border border-zinc-800 animate-pulse" />;
  }
  if (!stats) return null;

  const progress = stats.progress || { xp_this_level: 0, xp_needed: 100 };
  const pct = Math.min(100, Math.max(0, (progress.xp_this_level / Math.max(progress.xp_needed, 1)) * 100));

  return (
    <div className="space-y-4">
      <div
        data-testid="stats-panel"
        className="rounded-xl bg-gradient-to-br from-zinc-900 via-zinc-900 to-rose-950/10 border border-rose-500/15 p-5"
      >
        <div className="flex items-start gap-4 flex-wrap">
          {/* Level */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div
              data-testid="stats-level-badge"
              className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 text-black font-heading font-bold text-xl flex items-center justify-center shadow-lg shadow-rose-500/20"
            >
              {stats.level}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-rose-400/80 font-mono">Level {stats.level}</p>
              <p className="font-heading text-lg text-zinc-100" data-testid="stats-xp">
                {stats.xp.toLocaleString()} <span className="text-sm text-zinc-500 font-normal">XP</span>
              </p>
            </div>
          </div>

          {/* Streak */}
          <div className="flex items-center gap-2 flex-shrink-0 bg-zinc-950/60 border border-orange-500/20 rounded-lg px-3 py-2">
            <Flame className={`w-5 h-5 ${stats.current_streak > 0 ? "text-orange-400" : "text-zinc-600"}`} />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-orange-400/80 font-mono">Streak</p>
              <p className="font-heading text-base text-zinc-100" data-testid="stats-streak">
                {stats.current_streak} day{stats.current_streak === 1 ? "" : "s"}
                {stats.longest_streak > stats.current_streak && (
                  <span className="ml-1 text-[10px] text-zinc-500">best {stats.longest_streak}</span>
                )}
              </p>
            </div>
          </div>

          {/* Badges count */}
          <Link
            to="/profile"
            className="flex items-center gap-2 flex-shrink-0 bg-zinc-950/60 border border-amber-500/20 rounded-lg px-3 py-2 hover:border-amber-500/40 transition-colors"
          >
            <Trophy className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-amber-400/80 font-mono">Badges</p>
              <p className="font-heading text-base text-zinc-100" data-testid="stats-badges-count">
                {unlocked.length}
                <span className="text-zinc-500 text-sm">/{allBadges.length}</span>
              </p>
            </div>
          </Link>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              {progress.xp_this_level} / {progress.xp_needed} XP to level {stats.level + 1}
            </span>
            <span className="text-[10px] text-zinc-600">{Math.round(pct)}%</span>
          </div>
          <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
            <div
              data-testid="stats-xp-progress"
              className="h-full bg-gradient-to-r from-rose-500 to-amber-400 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Recent badges strip */}
        {unlocked.length > 0 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {unlocked.slice(0, 6).map((b) => (
              <div key={b.id} data-testid={`badge-${b.id}`} className="flex items-center gap-1.5 bg-zinc-950/50 border border-zinc-800 rounded-full px-2 py-1">
                <BadgeIcon badge={b} size={18} />
                <span className="text-[11px] text-zinc-300">{b.label}</span>
              </div>
            ))}
            {unlocked.length > 6 && (
              <Link to="/profile" className="text-[11px] text-zinc-500 hover:text-zinc-200 underline underline-offset-2">
                +{unlocked.length - 6} more
              </Link>
            )}
          </div>
        )}
      </div>

      {showBadgeGallery && (
        <div data-testid="badge-gallery" className="rounded-xl bg-zinc-900/60 border border-white/5 p-4">
          <h3 className="font-heading text-sm font-semibold text-zinc-100 mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" /> All Badges
            <span className="ml-auto text-[11px] text-zinc-500 font-mono">{unlocked.length} / {allBadges.length}</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...unlocked, ...locked].map((b) => {
              const isUnlocked = unlockedSet.has(b.id);
              return (
                <div
                  key={b.id}
                  data-testid={`gallery-badge-${b.id}${isUnlocked ? "" : "-locked"}`}
                  className={`flex items-start gap-2 p-2 rounded-lg border ${
                    isUnlocked
                      ? "bg-zinc-950/60 border-zinc-800"
                      : "bg-zinc-900/30 border-zinc-800/50"
                  }`}
                >
                  <BadgeIcon badge={b} locked={!isUnlocked} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${isUnlocked ? "text-zinc-100" : "text-zinc-500"}`}>
                      {b.label}
                    </p>
                    <p className={`text-[10px] leading-tight mt-0.5 ${isUnlocked ? "text-zinc-500" : "text-zinc-600"}`}>
                      {b.description}
                    </p>
                    <p className="text-[9px] text-rose-400/70 font-mono mt-0.5">+{b.xp_reward} XP</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
